import { StringObject } from "../../../objects/StringObject";
import {
  OverlayBaseMetrics,
  StringDevOverlayBadgeState,
  StringDevOverlayModule,
} from "./StringDevOverlayModule";
import { StringDevModule } from "./StringDevModule";

export interface StringDevBadgeMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
}

export interface StringDevBadgeEntry<TExtra = undefined> {
  object: StringObject;
  targetId: string;
  badge: HTMLButtonElement;
  depth: number;
  extra: TExtra;
  cleanup: Array<() => void>;
}

export abstract class StringDevBadgeOverlayModule<
  TExtra = undefined,
  TMeasurement extends StringDevBadgeMeasurement = StringDevBadgeMeasurement,
> extends StringDevOverlayModule<StringDevBadgeEntry<TExtra>, TMeasurement> {
  /** Override to define the data-attribute on the badge element. Falls back to devtool.overlay.badgeAttribute. */
  protected get badgeAttribute(): string {
    return (this.constructor as typeof StringDevModule).devtool?.overlay?.badgeAttribute ?? "";
  }

  protected createOverlayEntry(object: StringObject): StringDevBadgeEntry<TExtra> {
    const targetId = this.resolveOverlayTargetId(object);
    const depth = this.resolveBadgeDepth(object);
    const badge = this.createOverlayBadge({
      targetId,
      selectorAttribute: this.badgeAttribute,
      depth,
      attributes: this.getInitialBadgeAttributes(object, targetId, depth),
    });
    const cleanup: Array<() => void> = [];

    const onClick = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      this.onBadgeClick(entry, event);
    };

    badge.addEventListener("click", onClick);
    cleanup.push(() => badge.removeEventListener("click", onClick));

    const entry: StringDevBadgeEntry<TExtra> = {
      object,
      targetId,
      badge,
      depth,
      extra: this.createBadgeExtra(object, badge, targetId, depth),
      cleanup,
    };

    const subscriptions = this.bindBadge(entry);
    if (subscriptions) {
      cleanup.push(...subscriptions);
    }

    this.applyBadgeEnabledState(entry);
    this.renderBadge(entry);
    this.viewportLayer.getWorldElement(this.getWorldHost()).appendChild(badge);
    return entry;
  }

  protected destroyOverlayEntry(entry: StringDevBadgeEntry<TExtra>): void {
    for (const cleanup of entry.cleanup) {
      cleanup();
    }
    this.destroyBadgeExtra(entry);
    entry.badge.remove();
  }

  protected measureEntry(
    _entry: StringDevBadgeEntry<TExtra>,
    metrics: OverlayBaseMetrics,
  ): TMeasurement {
    if (!metrics.visible) {
      return {
        visible: false,
        docX: 0,
        docY: 0,
      } as TMeasurement;
    }

    return {
      visible: true,
      docX: metrics.baseAnchorX,
      docY: metrics.baseAnchorY + metrics.collisionOffset,
    } as TMeasurement;
  }

  protected applyMeasurement(
    entry: StringDevBadgeEntry<TExtra>,
    measurement: TMeasurement | undefined,
  ): void {
    this.applyOverlayBadgePosition(entry.badge, measurement);
    this.afterBadgeMeasurement(entry, measurement);
  }

  protected onEnabledChange(_enabled: boolean): void {
    for (const entry of this.entries.values()) {
      this.applyBadgeEnabledState(entry);
      this.renderBadge(entry);
    }
  }

  protected createBadgeExtra(
    _object: StringObject,
    _badge: HTMLButtonElement,
    _targetId: string,
    _depth: number,
  ): TExtra {
    return undefined as TExtra;
  }

  protected destroyBadgeExtra(_entry: StringDevBadgeEntry<TExtra>): void {}

  protected bindBadge(_entry: StringDevBadgeEntry<TExtra>): Array<() => void> | void {}

  protected resolveBadgeDepth(object: StringObject): number {
    return this.resolveOverlayObjectDepth(object.htmlElement);
  }

  protected getInitialBadgeAttributes(
    _object: StringObject,
    _targetId: string,
    _depth: number,
  ): Record<string, string | boolean | number | null | undefined> | undefined {
    return undefined;
  }

  protected applyBadgeEnabledState(entry: StringDevBadgeEntry<TExtra>): void {
    this.applyOverlayBadgeState(entry.badge, {
      disabled: !this.enabled,
      attributes: {
        "data-module-enabled": this.enabled,
      },
    });
  }

  protected renderBadge(entry: StringDevBadgeEntry<TExtra>): void {
    this.applyOverlayBadgeState(entry.badge, this.getBadgeState(entry));
  }

  protected afterBadgeMeasurement(
    _entry: StringDevBadgeEntry<TExtra>,
    _measurement: TMeasurement | undefined,
  ): void {}

  protected abstract onBadgeClick(
    entry: StringDevBadgeEntry<TExtra>,
    event: MouseEvent,
  ): void;

  protected abstract getBadgeState(
    entry: StringDevBadgeEntry<TExtra>,
  ): StringDevOverlayBadgeState;
}
